/**
 * @param {{ endpoint: string, duration: number, title?: string, status?: string|number }} args
 */
export function relicApiSuccess({ endpoint, duration, title = '', status = '200' } = {}) {
    newrelic.addPageAction('API_SUCCESS', {
        endpoint,
        status: String(status),
        duration:Math.round(duration),
        title,
        application: 'legacy-frontend',
    });
}

/**
 * @param {{ endpoint: string, duration: number, title?: string, status?: string|number }} args
 */
export function relicApiFail({ endpoint, duration, title = '', status = '400' } = {}) {
    newrelic.addPageAction('API_FAIL', {
        endpoint,
        status: String(status),
        duration:Math.round(duration),
        title,
        application: 'legacy-frontend',
    });
}

/**
 * totalDuration — from create API start through create response and attachment uploads (emit on PASS after uploads).
 * caseCreation / duration — create API time only.
 * @param {{ title: string, totalDuration: number, caseCreation: number, caseStatus: 'PASS'|'FAIL', duration: number, endpoint: string, errorMessage?: string }} args
 */
export function relicCaseCreation({
    title,
    totalDuration,
    caseCreation,
    caseStatus,
    duration,
    endpoint,
    payload,
    errorMessage,
}) {
    if (typeof newrelic === 'undefined' || typeof newrelic.addPageAction !== 'function') {
        return;
    }
    
    const payloadData = {
        title,
        totalDuration: Math.round(totalDuration),
        caseCreation: Math.round(caseCreation),
        caseStatus,
        duration: Math.round(duration),
        endpoint: endpoint || '',
        application: 'legacy-frontend',
        payload
    };
    if (errorMessage) {
        payloadData.errorMessage = String(errorMessage);
    }

    newrelic.addPageAction('CASE_CREATION', payloadData);
}

/**
 * Normalizes an HTTP status into a single, dashboard-friendly failure bucket.
 * `0` means the request never got a response (network / CORS / edge) â€” i.e. the
 * browser-level "Failed to fetch", which is NOT a MILO API response.
 * @param {number} httpStatus
 * @returns {'none'|'network'|'auth_token'|'forbidden_org'|'payload_too_large'|'bad_request'|'server_error'|'unknown'}
 */
export function classifyCaseFailure(httpStatus) {
    if (httpStatus >= 200 && httpStatus < 300) return 'none';
    if (httpStatus === 0) return 'network';            // no response (e.g. "Failed to fetch")
    if (httpStatus === 401) return 'auth_token';       // expired / empty bearer â€” client side
    if (httpStatus === 403) return 'forbidden_org';    // org-member / permission validation
    if (httpStatus === 413) return 'payload_too_large';// attachment payload
    if (httpStatus === 400) return 'bad_request';      // header / ims-scope / malformed
    if (httpStatus >= 500) return 'server_error';      // MILO-side error
    return 'unknown';
}

/**
 * Reliable, single-schema telemetry for every case-creation attempt (pass and fail).
 * Unlike API_FAIL (which defaults status to '400' and drops the error text), this captures
 * the REAL http status, a normalized failureType, and enough client context (token / org /
 * flow / MILO request id) to triage a failure without digging through raw AjaxRequest events.
 * Emits the `CASE_CREATION_RESULT` PageAction.
 * @param {{
 *   flow: string,
 *   success: boolean,
 *   httpStatus: number,
 *   errorMessage?: string,
 *   errorClass?: string,
 *   durationMs?: number,
 *   orgId?: string,
 *   orgSource?: string,
 *   token?: { token?: string, expire?: Date|number } | null,
 *   payload?: object | null,
 *   res?: Response | null,
 * }} args
 */
export function relicCaseCreationResult({
    flow = '',
    success = false,
    httpStatus = 0,
    errorMessage = '',
    errorClass = '',
    durationMs = 0,
    orgId = '',
    orgSource = '',
    token = null,
    xRequestID = '',
    payload: caseCreationPayload = null,
    res = null,
} = {}) {
    if (typeof newrelic === 'undefined' || typeof newrelic.addPageAction !== 'function') {
        return;
    }

    const status = Number(httpStatus) || 0;
    const expireMs = token?.expire instanceof Date
        ? token.expire.getTime()
        : Number(token?.expire) || 0;

    const eventPayload = {
        application: 'legacy-frontend',
        flow,
        caseStatus: success ? 'PASS' : 'FAIL',
        httpStatus: status,
        failureType: success ? 'none' : classifyCaseFailure(status),
        durationMs: Math.round(durationMs) || 0,
        orgId: orgId || '',
        orgSource: orgSource || '',
        tokenPresent: !!token?.token,
        tokenExpiresInSec: expireMs ? Math.round((expireMs - Date.now()) / 1000) : -1,
    };

    if (caseCreationPayload != null) {
        eventPayload.payload = caseCreationPayload;
    }

    const reqId = xRequestID
        || res?.headers?.get?.('x-request-id')
        || res?.headers?.get?.('x-gw-request-id')
        || res?.headers?.get?.('apigw-requestid')
        || '';

    if (reqId) {
        eventPayload.miloRequestID = reqId;
    }

    if (!success && errorMessage) eventPayload.errorMessage = String(errorMessage);
    if (!success && errorClass) eventPayload.errorClass = String(errorClass);

    newrelic.addPageAction('CASE_CREATION_RESULT', eventPayload);
}

/**
 * Tag the active support view for New Relic so page views / web vitals / all
 * events are attributable per view instead of collapsing onto the homepage URL.
 * The view lives only in `?support-tab=<view>`, which NR strips from pageUrl,
 * so we name it explicitly here.
 * @param {string} tab current support-tab value (e.g. 'home', 'my-cases', 'open-ticket')
 */
export function relicSetView(tab) {
    if (typeof newrelic === 'undefined') {
        return;
    }
    let view = tab || 'home';
    // my-cases list vs a single open case = two distinct pages (same signal the
    // app uses to load support-case-detail: support-tab-resources.js).
    if (view === 'my-cases' && new URL(window.location.href).searchParams.has('case-id')) {
        view = 'case-details';
    }
    if (typeof newrelic.setPageViewName === 'function') {
        newrelic.setPageViewName(`support/${view}`);
    }
    if (typeof newrelic.setCurrentRouteName === 'function') {
        newrelic.setCurrentRouteName(`support/${view}`);
    }
    if (typeof newrelic.setCustomAttribute === 'function') {
        newrelic.setCustomAttribute('supportTab', view);
    }
}
